import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Apple,
  ArrowLeft,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  FileText,
  Home,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  Play,
  Scissors,
  ShieldCheck,
  Smartphone,
  Trash2,
  UserCog,
  UsersRound,
  X,
} from 'lucide-react';
import './App.css';
import heroScreenshot from './screen_shot.png';
import logo from './logo.png';
import {
  getSafeContinueUrl,
  parseResetQuery,
  RESET_PASSWORD_PAGE,
  submitPasswordReset,
  verifyResetLink,
} from './firebaseReset';
import { submitAccountDeletionRequest } from './firebaseAccount';

const contact = {
  phone: '7300384779',
  email: 'tailorwalaapp@gmail.com',
};

const downloadLinks = {
  android: 'https://play.google.com/store/apps/details?id=com.tailorwalaapp.shop',
  ios: '#download-ios',
};

const navItems = [
  { label: 'Home', page: 'home', icon: Home },
  { label: 'About', page: 'about', icon: UsersRound },
  { label: 'Contact', page: 'contact', icon: Phone },
  { label: 'Privacy', page: 'privacy', icon: ShieldCheck },
  { label: 'Terms', page: 'terms', icon: FileText },
  { label: 'Delete Account', page: 'delete-account', icon: Trash2 },
];

const RESET_MIN_PASSWORD_LENGTH = 6;

const features = [
  {
    icon: UsersRound,
    title: 'Customer Records',
    text: 'Name, phone number, measurements, and order history stay easy to find when the shop gets busy.',
  },
  {
    icon: ClipboardList,
    title: 'Order Tracking',
    text: 'Track pending, in-progress, completed, and delivered stitching work with due dates and payment notes.',
  },
  {
    icon: UserCog,
    title: 'Worker Management',
    text: 'Assign orders to workers and keep daily tailoring work organized from one simple dashboard.',
  },
];

const workflowSteps = [
  'Add customer with phone and measurements',
  'Create order with price, due date and worker',
  'Track pending fees, delivery status and recent work',
  'Use bilingual labels for shop teams and customers',
];

const aboutHighlights = [
  'Built around real tailoring shop workflows',
  'Designed for bilingual English + Hindi usage',
  'Focused on customers, orders, workers and payments',
  'Simple enough for daily repeated use',
];

const policySections = [
  {
    title: 'Welcome to TailorWala',
    points: [
      'Your privacy is important to us. This Privacy Policy explains how TailorWala collects, uses, stores, and protects your information when you use our mobile application and services.',
      'If you do not agree with this Privacy Policy, please do not use the application.',
    ],
  },
  {
    title: '1. Information We Collect',
    points: [
      'We may collect the following types of information:',
    ],
  },
  {
    title: 'a) Personal Information',
    points: [
      'Name.',
      'Mobile number.',
      'Email address.',
      'Address or delivery location.',
      'Profile information provided by the user.',
    ],
  },
  {
    title: 'b) Tailoring & Order Information',
    points: [
      'Measurement details entered by users.',
      'Stitching preferences.',
      'Order history.',
      'Uploaded images or design references.',
    ],
  },
  {
    title: 'c) Device Information',
    points: [
      'Device model.',
      'Operating system version.',
      'IP address.',
      'App version.',
      'Crash logs and analytics.',
    ],
  },
  {
    title: 'd) Payment Information',
    points: [
      'Currently, TailorWala does not process payments inside the app and services may be offered free of cost.',
      'In the future, the app may introduce paid features, subscription plans, tailoring service charges, or online payment options through trusted third-party payment providers. If payment features are added later, this Privacy Policy may be updated accordingly.',
      'TailorWala does not store complete debit or credit card information on its own servers.',
    ],
  },
  {
    title: '2. How We Use Your Information',
    points: [
      'Create and manage your account.',
      'Process tailoring or stitching orders.',
      'Contact users regarding orders or support.',
      'Improve app performance and user experience.',
      'Provide customer support.',
      'Send important updates and notifications.',
      'Prevent fraud and misuse.',
      'Comply with legal obligations.',
    ],
  },
  {
    title: '3. Permissions Used in the App',
    points: [
      'Depending on app features, TailorWala may request permissions such as:',
      'Camera permission for uploading clothing or design images.',
      'Storage/gallery access for selecting photos.',
      'Internet permission for app functionality.',
      'Notification permission for order updates.',
      'Location permission only if delivery or location services are used.',
      'Permissions are used only for app-related functionality.',
    ],
  },
  {
    title: '4. Data Sharing',
    points: [
      'We do not sell your personal information.',
      'Your data may be shared only with tailoring partners or delivery partners for order fulfillment.',
      'Your data may be shared only with trusted third-party service providers.',
      'Your data may be shared only if required by law or legal process.',
      'Your data may be shared only to protect app security, users, or company rights.',
    ],
  },
  {
    title: '5. Data Security',
    points: [
      'We use reasonable security measures to protect user data from unauthorized access, misuse, or disclosure. However, no online platform can guarantee complete security.',
    ],
  },
  {
    title: '6. Data Retention',
    points: [
      'We retain user data only as long as necessary for providing services, legal compliance, dispute resolution, or business purposes.',
    ],
  },
  {
    title: "7. Children's Privacy",
    points: [
      'TailorWala is not intended for children under 13 years of age. We do not knowingly collect personal information from children.',
    ],
  },
  {
    title: '8. Third-Party Services',
    points: [
      'The app may use third-party services such as:',
      'Google Play Services.',
      'Firebase Analytics.',
      'Payment gateways.',
      'Delivery or communication services.',
      'These services may collect information according to their own privacy policies.',
    ],
  },
  {
    title: '9. User Rights',
    points: [
      'Users may access their account information.',
      'Users may request correction of inaccurate data.',
      'Users may request deletion of their account and data.',
      'Users may contact us regarding privacy concerns.',
      'To request account deletion or privacy support, contact us using the details below.',
    ],
  },
  {
    title: '10. Contact Us',
    points: [
      'For questions related to this Privacy Policy, contact us at tailorwalaapp@gmail.com.',
      'Phone: +91 7300384779.',
    ],
  },
  {
    title: '11. Changes to This Privacy Policy',
    points: [
      'We may update this Privacy Policy from time to time.',
      'Updated versions will be posted inside the app or on the official website.',
      'By continuing to use TailorWala, you agree to the updated policy.',
    ],
  },
];

const termsSections = [
  {
    title: 'TailorWala - Terms and Conditions',
    points: [
      'Effective Date: 10 May 2026.',
      'These Terms and Conditions govern the use of the TailorWala mobile application and services.',
      'By accessing or using TailorWala, you agree to comply with these Terms.',
    ],
  },
  {
    title: '1. Acceptance of Terms',
    points: [
      'By using TailorWala, you confirm that you are legally capable of entering into an agreement.',
      'You will use the app only for lawful purposes.',
      'You agree to all terms mentioned in this document.',
    ],
  },
  {
    title: '2. Services',
    points: [
      'TailorWala provides tailoring-related services including:',
      'Stitching order management.',
      'Measurement handling.',
      'Design or clothing references.',
      'Customer-tailor communication.',
      'Delivery or pickup coordination if available.',
      'Services may change or improve over time.',
    ],
  },
  {
    title: '3. User Accounts',
    points: [
      'Users are responsible for providing accurate information.',
      'Users are responsible for maintaining account confidentiality.',
      'Users are responsible for activities performed through their account.',
      'TailorWala may suspend or terminate accounts involved in misuse, fraud, abuse, or illegal activity.',
    ],
  },
  {
    title: '4. Orders and Measurements',
    points: [
      'Users are responsible for providing accurate measurements and order details.',
      'TailorWala is not responsible for fitting issues caused by incorrect measurements submitted by users.',
    ],
  },
  {
    title: '5. Payments and Future Paid Services',
    points: [
      'Currently, TailorWala does not charge users for app usage or services inside the application.',
      'In the future, TailorWala may introduce:',
      'Paid tailoring services.',
      'Premium features.',
      'Subscription plans.',
      'Online payment options.',
      'Delivery or convenience charges.',
      'If payment functionality is added later:',
      'Payments may be processed through trusted third-party payment gateways.',
      'Applicable pricing and refund rules will be clearly communicated to users.',
      'TailorWala reserves the right to reject suspicious or fraudulent transactions.',
    ],
  },
  {
    title: '6. Prohibited Activities',
    points: [
      'Users must not:',
      'Users must not use the app for illegal purposes.',
      'Users must not upload harmful, abusive, or copyrighted content without permission.',
      'Users must not attempt unauthorized access to the app or servers.',
      'Users must not interfere with app functionality or security.',
      "Users must not misuse other users' information.",
    ],
  },
  {
    title: '7. Intellectual Property',
    points: [
      'All app content including logos, branding, text, graphics, and software belongs to TailorWala unless otherwise stated.',
      'Users may not copy, reproduce, or distribute app content without permission.',
    ],
  },
  {
    title: '8. Limitation of Liability',
    points: [
      'TailorWala is provided on an "as available" basis.',
      'We are not liable for:',
      'Delays caused by third parties.',
      'Technical interruptions.',
      'Data loss beyond reasonable control.',
      'Indirect or incidental damages.',
    ],
  },
  {
    title: '9. Termination',
    points: [
      'We may suspend or terminate access to the app at any time if users violate these Terms.',
    ],
  },
  {
    title: '10. Third-Party Links and Services',
    points: [
      'The app may contain links or integrations with third-party services. TailorWala is not responsible for third-party policies or content.',
    ],
  },
  {
    title: '11. Changes to Terms',
    points: [
      'We may update these Terms and Conditions at any time.',
      'Continued use of the app after updates means you accept the revised Terms.',
    ],
  },
  {
    title: '12. Governing Law',
    points: [
      'These Terms shall be governed by the laws of India.',
    ],
  },
  {
    title: '13. Contact Information',
    points: [
      'For support or legal queries, email tailorwalaapp@gmail.com.',
      'Phone: +91 7300384779.',
    ],
  },
  {
    title: 'App Store Compliance Note',
    points: [
      'This Privacy Policy and Terms document is written to support standard compliance requirements for publishing apps on platforms such as:',
      'Google Play Store.',
      'Apple App Store.',
      'You should also ensure that:',
      'You should ensure that your app contains an in-app privacy policy link.',
      'You should ensure that an account deletion option is available if account creation exists.',
      'You should ensure permissions used in the app match the declared policy.',
      'You should ensure the Play Console Data Safety section is filled correctly.',
    ],
  },
];

const privacyPageSections = [...policySections, ...termsSections];

const pageCopy = {
  about: {
    eyebrow: 'About Us',
    title: 'Built to digitize everyday tailoring work.',
    text: 'Tailor Wala App is a smart and easy-to-use platform designed to bridge the gap between tailors and customers. Our mission is to help tailors grow their business while making customer records, order details, measurements and delivery tracking simple.',
  },
  contact: {
    eyebrow: 'Contact Us',
    title: 'Need help, support or product details?',
    text: 'Reach out for support, feedback, partnership questions or help using Tailor Wala App in your tailoring business.',
  },
  privacy: {
    eyebrow: 'Privacy Policy',
    title: 'TailorWala - Privacy Policy',
    text: 'This Privacy Policy explains how TailorWala collects, uses, stores, and protects your information when you use our mobile application and services.',
  },
  terms: {
    eyebrow: 'Terms & Conditions',
    title: 'TailorWala - Terms and Conditions',
    text: 'These Terms and Conditions govern the use of the TailorWala mobile application and services. By accessing or using TailorWala, you agree to comply with these Terms.',
  },
  'delete-account': {
    eyebrow: 'Delete Account',
    title: 'Request deletion of your TailorWala account.',
    text: 'Enter the email linked with your TailorWala account. We will record your deletion request in Firebase and process it after verification.',
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

const staggerGroup = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const sectionMotion = {
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, amount: 0.22 },
  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
};

function getPathForPage(page) {
  return page === 'home' ? '/' : `/${page}`;
}

function getPageFromLocation() {
  const resetState = parseResetQuery(window.location.search);
  const page = window.location.pathname.replace(/^\/+|\/+$/g, '') || 'home';

  if (resetState.mode === 'resetPassword' || page === RESET_PASSWORD_PAGE) {
    return RESET_PASSWORD_PAGE;
  }

  return navItems.some((item) => item.page === page) ? page : 'home';
}

function navigateToPage(page, event) {
  event.preventDefault();
  window.history.pushState({}, '', getPathForPage(page));
  window.dispatchEvent(new Event('popstate'));
}

function App() {
  const [page, setPage] = useState(getPageFromLocation);
  const [locationSearch, setLocationSearch] = useState(() => window.location.search);
  const [showHeroScreenshot, setShowHeroScreenshot] = useState(Boolean(heroScreenshot));
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const syncPage = () => {
      setPage(getPageFromLocation());
      setLocationSearch(window.location.search);
      setIsMenuOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    window.addEventListener('popstate', syncPage);
    return () => window.removeEventListener('popstate', syncPage);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('menu-open', isMenuOpen);
    return () => document.body.classList.remove('menu-open');
  }, [isMenuOpen]);

  const handleNavClick = (targetPage, event) => {
    navigateToPage(targetPage, event);
    setIsMenuOpen(false);
  };

  return (
    <main className="site-shell">
      <motion.nav
        className="topbar"
        aria-label="Main navigation"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
      >
        <a
          href="/"
          className="brand"
          aria-label="Tailor Wala App home"
          onClick={(event) => handleNavClick('home', event)}
        >
          <span className="brand-mark">
            <img src={logo} alt="" aria-hidden="true" />
          </span>
          <span>Tailor Wala App</span>
        </a>
        <div className="nav-links">
          {navItems.map((item) => (
            <a
              href={getPathForPage(item.page)}
              className={page === item.page ? 'active-nav' : undefined}
              onClick={(event) => handleNavClick(item.page, event)}
              key={item.page}
            >
              {item.label}
            </a>
          ))}
        </div>
        <button
          className="menu-toggle"
          type="button"
          aria-label="Open menu"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(true)}
        >
          <Menu size={22} strokeWidth={2.5} />
        </button>
      </motion.nav>

      <div
        className={`menu-backdrop ${isMenuOpen ? 'is-open' : ''}`}
        role="presentation"
        onClick={() => setIsMenuOpen(false)}
      />
      <aside className={`mobile-drawer ${isMenuOpen ? 'is-open' : ''}`} aria-hidden={!isMenuOpen}>
        <div className="drawer-header">
          <a
            href="/"
            className="brand"
            aria-label="Tailor Wala App home"
            onClick={(event) => handleNavClick('home', event)}
          >
            <span className="brand-mark">
              <img src={logo} alt="" aria-hidden="true" />
            </span>
            <span>Tailor Wala App</span>
          </a>
          <button
            className="drawer-close"
            type="button"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            <X size={22} strokeWidth={2.5} />
          </button>
        </div>
        <nav className="drawer-links" aria-label="Mobile navigation">
          {navItems.map((item) => {
            const NavIcon = item.icon;

            return (
              <a
                href={getPathForPage(item.page)}
                className={page === item.page ? 'active-drawer-link' : undefined}
                onClick={(event) => handleNavClick(item.page, event)}
                key={item.page}
              >
                <span className="drawer-link-icon">
                  <NavIcon size={19} strokeWidth={2.4} />
                </span>
                <span>{item.label}</span>
              </a>
            );
          })}
        </nav>
      </aside>

      {page === 'home' ? (
        <HomePage
          showHeroScreenshot={showHeroScreenshot}
          setShowHeroScreenshot={setShowHeroScreenshot}
        />
      ) : null}
      {page === 'about' ? <AboutPage /> : null}
      {page === 'contact' ? <ContactPage /> : null}
      {page === 'privacy' ? <LegalPage type="privacy" sections={privacyPageSections} /> : null}
      {page === 'terms' ? <LegalPage type="terms" sections={termsSections} /> : null}
      {page === 'delete-account' ? <DeleteAccountPage /> : null}
      {page === RESET_PASSWORD_PAGE ? <ResetPasswordPage search={locationSearch} /> : null}

      <footer className="footer">
        <span>Tailor Wala App</span>
        <span>© 2026 Tailor Wala App. All rights reserved.</span>
      </footer>
    </main>
  );
}

function ResetPasswordPage({ search }) {
  const [status, setStatus] = useState('checking');
  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resetState = useMemo(() => parseResetQuery(search), [search]);
  const safeContinueUrl = getSafeContinueUrl(resetState.continueUrl);

  useEffect(() => {
    let isMounted = true;

    async function validateResetLink() {
      if (resetState.mode !== 'resetPassword' || !resetState.oobCode) {
        if (isMounted) {
          setStatus('invalid');
          setErrorMessage(
            'This password reset link is incomplete. Please request a new reset email from the app.',
          );
        }
        return;
      }

      try {
        const email = await verifyResetLink(resetState);

        if (isMounted) {
          setAccountEmail(email);
          setStatus('ready');
        }
      } catch (error) {
        if (isMounted) {
          setStatus('invalid');
          setErrorMessage(
            'This reset link is invalid or expired. Please request a fresh password reset email.',
          );
        }
      }
    }

    setStatus('checking');
    setErrorMessage('');
    setSuccessMessage('');
    validateResetLink();

    return () => {
      isMounted = false;
    };
  }, [resetState]);

  async function handleResetSubmit(event) {
    event.preventDefault();

    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    if (trimmedPassword.length < RESET_MIN_PASSWORD_LENGTH) {
      setErrorMessage(
        `Password must be at least ${RESET_MIN_PASSWORD_LENGTH} characters long.`,
      );
      return;
    }

    if (trimmedPassword !== trimmedConfirmPassword) {
      setErrorMessage('Password and confirm password must match.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');
      await submitPasswordReset(resetState, trimmedPassword);
      setStatus('success');
      setSuccessMessage(
        'Password updated successfully. Open Tailor Wala App and login with your new password.',
      );
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Unable to update password. Please request a fresh reset link and try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <motion.header
        className="page-hero"
        initial="hidden"
        animate="visible"
        variants={staggerGroup}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.a
          className="back-link"
          href="/"
          onClick={(event) => navigateToPage('home', event)}
          variants={fadeUp}
        >
          <ArrowLeft size={18} strokeWidth={2.4} />
          Back to home
        </motion.a>
        <motion.p className="eyebrow" variants={fadeUp}>
          Password Reset
        </motion.p>
        <motion.h1 variants={fadeUp}>Set a new password for Tailor Wala App.</motion.h1>
        <motion.p className="page-hero-text" variants={fadeUp}>
          Use the secure reset link from your email to create a fresh password and
          then sign in again from the mobile app.
        </motion.p>
      </motion.header>

      <motion.section className="section reset-page-grid" {...sectionMotion}>
        <motion.div className="reset-side-card" variants={fadeUp}>
          <div className="reset-icon-chip">
            <ShieldCheck size={20} strokeWidth={2.4} />
          </div>
          <h3>Before you continue</h3>
          <p>
            This reset link works only for the email account that requested it.
            Choose a strong password with at least 6 characters.
          </p>
          <div className="reset-checklist">
            <div className="check-row">
              <CheckCircle2 size={18} strokeWidth={2.4} />
              <span>Reset happens securely through Firebase Authentication.</span>
            </div>
            <div className="check-row">
              <CheckCircle2 size={18} strokeWidth={2.4} />
              <span>After success, open Tailor Wala App and login again.</span>
            </div>
            <div className="check-row">
              <CheckCircle2 size={18} strokeWidth={2.4} />
              <span>If the link expires, request a new forgot-password email.</span>
            </div>
          </div>
        </motion.div>

        <motion.div className="reset-form-card" variants={fadeUp}>
          <div className="reset-card-header">
            <div className="reset-icon-chip warm-chip">
              <Mail size={20} strokeWidth={2.4} />
            </div>
            <div>
              <p className="reset-label">Account</p>
              <h3>{accountEmail || 'Tailor Wala App user'}</h3>
            </div>
          </div>

          {status === 'checking' ? (
            <div className="reset-state-card">
              <p className="reset-label">Checking link</p>
              <p className="reset-body">
                Verifying your secure password reset link. Please wait for a moment.
              </p>
            </div>
          ) : null}

          {status === 'invalid' ? (
            <div className="reset-state-card error-state">
              <p className="reset-label">Link problem</p>
              <p className="reset-body">{errorMessage}</p>
              <a className="secondary-action reset-inline-action" href="/">
                Go to website home
              </a>
            </div>
          ) : null}

          {status === 'ready' ? (
            <form className="reset-form" onSubmit={handleResetSubmit}>
              <label className="reset-field">
                <span>New password</span>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
              </label>
              <label className="reset-field">
                <span>Confirm password</span>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
              </label>

              {errorMessage ? <p className="reset-feedback error-text">{errorMessage}</p> : null}

              <div className="reset-action-row">
                <button
                  type="submit"
                  className="primary-action reset-submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating password...' : 'Update password'}
                </button>
                <a className="secondary-action" href={safeContinueUrl}>
                  Cancel
                </a>
              </div>
            </form>
          ) : null}

          {status === 'success' ? (
            <div className="reset-state-card success-state">
              <p className="reset-label">Done</p>
              <p className="reset-body">{successMessage}</p>
              <div className="reset-action-row">
                <a className="primary-action reset-submit-button" href={safeContinueUrl}>
                  Continue
                </a>
                <a className="secondary-action" href="/">
                  Website home
                </a>
              </div>
            </div>
          ) : null}
        </motion.div>
      </motion.section>
    </>
  );
}

function HomePage({ showHeroScreenshot, setShowHeroScreenshot }) {
  return (
    <>
      <section className="hero" id="home">
        <motion.div
          className="hero-copy"
          variants={staggerGroup}
          initial="hidden"
          animate="visible"
        >
          <motion.p className="eyebrow" variants={fadeUp}>
            For tailors, boutiques and stitching shops
          </motion.p>
          <motion.h1 variants={fadeUp}>Tailor Wala App</motion.h1>
          <motion.p className="hero-text" variants={fadeUp}>
            Customers, measurements, orders, delivery dates, payments and workers
            in one clean app made for tailoring businesses.
          </motion.p>
          <motion.div className="download-actions" variants={fadeUp}>
            <motion.a
              className="store-button"
              href={downloadLinks.android}
              target="_blank"
              rel="noreferrer"
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.97 }}
              aria-label="Download Tailor Wala App for Android"
            >
              <span className="store-icon" aria-hidden="true">
                <Play size={20} fill="currentColor" strokeWidth={2.2} />
              </span>
              <span>
                <small>Get it on</small>
                Google Play
              </span>
            </motion.a>
            <motion.a
              className="store-button dark-store"
              href={downloadLinks.ios}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.97 }}
              aria-label="Download Tailor Wala App for iOS"
            >
              <span className="store-icon" aria-hidden="true">
                <Apple size={22} fill="currentColor" strokeWidth={2.2} />
              </span>
              <span>
                <small>Download on the</small>
                App Store
              </span>
            </motion.a>
          </motion.div>
          <motion.div className="trust-strip" aria-label="App highlights" variants={fadeUp}>
            <span>English + Hindi</span>
            <span>Order status</span>
            <span>Worker assign</span>
          </motion.div>
        </motion.div>

        <motion.div
          className="phone-showcase"
          aria-label="Tailor Wala App dashboard preview"
          initial={{ opacity: 0, y: 28, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 0.8, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            className="phone-frame"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          >
            {showHeroScreenshot ? (
              <img
                className="hero-screenshot"
                src={heroScreenshot}
                alt="Tailor Wala App screen preview"
                onError={() => setShowHeroScreenshot(false)}
              />
            ) : (
              <div className="phone-fallback">
                <div className="phone-header">
                  <Scissors size={16} color="var(--accent)" strokeWidth={2.4} />
                  <span className="phone-title">Dashboard</span>
                  <Smartphone size={16} color="var(--primary)" strokeWidth={2.4} />
                </div>
                <motion.div className="summary-card" whileHover={{ scale: 1.02 }}>
                  <p>Today Orders</p>
                  <strong>18</strong>
                  <span>6 ready for delivery</span>
                </motion.div>
                <div className="mini-grid">
                  <div>
                    <strong>245</strong>
                    <span>Customers</span>
                  </div>
                  <div>
                    <strong>128</strong>
                    <span>Orders</span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      </section>

      <motion.section className="section band enhanced-band" {...sectionMotion}>
        <motion.div className="section-heading" variants={fadeUp}>
          <p className="eyebrow">Why it helps</p>
          <h2>Shop ka kaam fast, clear aur organized.</h2>
        </motion.div>
        <motion.div className="feature-grid" variants={staggerGroup}>
          {features.map((feature) => {
            const FeatureIcon = feature.icon;

            return (
              <motion.article className="feature-card" key={feature.title} variants={fadeUp}>
                <span className="feature-icon">
                  <FeatureIcon size={22} strokeWidth={2.4} />
                </span>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </motion.article>
            );
          })}
        </motion.div>
      </motion.section>

      <motion.section className="section home-story" {...sectionMotion}>
        <motion.div className="story-copy" variants={fadeUp}>
          <p className="eyebrow">Daily Workflow</p>
          <h2>From customer entry to final delivery, everything stays visible.</h2>
          <p>
            Tailor Wala App gives tailoring shops a calmer operating system for
            repeated daily work. The app keeps the most important shop details
            close: customers, orders, due dates, workers and payments.
          </p>
        </motion.div>
        <motion.div className="workflow-list" variants={staggerGroup}>
          {workflowSteps.map((step, index) => (
            <motion.div className="workflow-row" key={step} variants={fadeUp}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <strong>{step}</strong>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      <motion.section className="section app-strip" {...sectionMotion}>
        <motion.div variants={fadeUp}>
          <p className="eyebrow">Ready for shops</p>
          <h2>Simple interface. Serious shop control.</h2>
        </motion.div>
        <motion.div className="metric-row" variants={staggerGroup}>
          {['Customers', 'Orders', 'Workers', 'Payments'].map((item) => (
            <motion.div className="metric-pill" key={item} variants={fadeUp}>
              <CheckCircle2 size={18} strokeWidth={2.4} />
              {item}
            </motion.div>
          ))}
        </motion.div>
      </motion.section>
    </>
  );
}

function PageHeader({ page }) {
  const copy = pageCopy[page];
  const isLegalPage = page === 'privacy' || page === 'terms';

  return (
    <motion.header
      className={`page-hero ${isLegalPage ? 'legal-hero' : ''}`}
      initial="hidden"
      animate="visible"
      variants={staggerGroup}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.a
        className="back-link"
        href="/"
        onClick={(event) => navigateToPage('home', event)}
        variants={fadeUp}
      >
        <ArrowLeft size={18} strokeWidth={2.4} />
        Back to home
      </motion.a>
      <motion.p className="eyebrow" variants={fadeUp}>
        {copy.eyebrow}
      </motion.p>
      <motion.h1 variants={fadeUp}>{copy.title}</motion.h1>
      <motion.p className="page-hero-text" variants={fadeUp}>
        {copy.text}
      </motion.p>
    </motion.header>
  );
}

function AboutPage() {
  return (
    <>
      <PageHeader page="about" />
      <motion.section className="section page-grid" {...sectionMotion}>
        <motion.div className="page-panel main-panel" variants={fadeUp}>
          <p>
            Tailor Wala App is made for shops that need a practical digital record
            of customer measurements, order status, worker assignment and pending
            fees. It brings the day-to-day tailoring notebook into a clean app
            without making the workflow feel complicated.
          </p>
          <p>
            Whether you are a professional tailor, boutique owner, or someone
            running a small stitching team, the app helps you work smarter and
            grow faster.
          </p>
        </motion.div>
        <motion.div className="page-panel highlight-panel" variants={staggerGroup}>
          {aboutHighlights.map((item) => (
            <motion.div className="check-row" key={item} variants={fadeUp}>
              <CheckCircle2 size={18} strokeWidth={2.4} />
              <span>{item}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>
    </>
  );
}

function ContactPage() {
  return (
    <>
      <PageHeader page="contact" />
      <motion.section className="section contact-page-grid" {...sectionMotion}>
        <motion.a href={`tel:${contact.phone}`} className="big-contact-card" variants={fadeUp}>
          <Phone size={24} strokeWidth={2.4} />
          <span>Phone</span>
          <strong>{contact.phone}</strong>
        </motion.a>
        <motion.a href={`mailto:${contact.email}`} className="big-contact-card" variants={fadeUp}>
          <Mail size={24} strokeWidth={2.4} />
          <span>Email</span>
          <strong>{contact.email}</strong>
        </motion.a>
        <motion.a
          href={`https://wa.me/${contact.phone}`}
          target="_blank"
          rel="noreferrer"
          className="big-contact-card accent-contact"
          variants={fadeUp}
        >
          <MessageCircle size={24} strokeWidth={2.4} />
          <span>WhatsApp</span>
          <strong>Chat with us</strong>
        </motion.a>
      </motion.section>
      <motion.section className="section support-note" {...sectionMotion}>
        <MapPin size={22} strokeWidth={2.4} />
        <div>
          <h2>Support for tailoring businesses</h2>
          <p>
            Message us with your shop name, issue, phone number and a short
            description so we can help faster.
          </p>
        </div>
      </motion.section>
    </>
  );
}

function DeleteAccountPage() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  async function handleDeleteRequest(event) {
    event.preventDefault();
    const trimmedEmail = email.trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setStatus('error');
      setMessage('Please enter a valid email address linked with your account.');
      return;
    }

    try {
      setStatus('submitting');
      setMessage('');
      await submitAccountDeletionRequest(trimmedEmail);
      setStatus('success');
      setMessage(
        'Your account deletion request has been submitted. We will verify the email and process the request as soon as possible.',
      );
      setEmail('');
    } catch (error) {
      setStatus('error');
      setMessage(
        'Unable to submit the request right now. Please email tailorwalaapp@gmail.com with your account email.',
      );
    }
  }

  return (
    <>
      <PageHeader page="delete-account" />
      <motion.section className="section reset-page-grid" {...sectionMotion}>
        <motion.div className="reset-side-card delete-side-card" variants={fadeUp}>
          <div className="reset-icon-chip danger-chip">
            <Trash2 size={20} strokeWidth={2.4} />
          </div>
          <h3>Before you request deletion</h3>
          <p>
            Account deletion is permanent after verification. Your customer,
            order, measurement, worker and profile data connected with the
            account may be removed as per our policy.
          </p>
          <div className="reset-checklist">
            <div className="check-row">
              <CheckCircle2 size={18} strokeWidth={2.4} />
              <span>Use the same email that you use in TailorWala App.</span>
            </div>
            <div className="check-row">
              <CheckCircle2 size={18} strokeWidth={2.4} />
              <span>We may contact you to verify ownership before deletion.</span>
            </div>
            <div className="check-row">
              <CheckCircle2 size={18} strokeWidth={2.4} />
              <span>You can also email us directly at tailorwalaapp@gmail.com.</span>
            </div>
          </div>
        </motion.div>

        <motion.div className="reset-form-card delete-form-card" variants={fadeUp}>
          <div className="reset-card-header">
            <div className="reset-icon-chip warm-chip">
              <Mail size={20} strokeWidth={2.4} />
            </div>
            <div>
              <p className="reset-label">Account email</p>
              <h3>Submit deletion request</h3>
            </div>
          </div>

          <form className="reset-form" onSubmit={handleDeleteRequest}>
            <label className="reset-field">
              <span>Email address</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Enter your registered email"
                autoComplete="email"
              />
            </label>

            {message ? (
              <p
                className={`reset-feedback ${
                  status === 'success' ? 'success-text' : 'error-text'
                }`}
              >
                {message}
              </p>
            ) : null}

            <div className="reset-action-row">
              <button
                type="submit"
                className="primary-action delete-submit-button"
                disabled={status === 'submitting'}
              >
                <Trash2 size={18} strokeWidth={2.4} />
                {status === 'submitting' ? 'Submitting...' : 'Delete Account'}
              </button>
              <a className="secondary-action" href={`mailto:${contact.email}`}>
                <Mail size={18} strokeWidth={2.4} />
                Email support
              </a>
            </div>
          </form>
        </motion.div>
      </motion.section>
    </>
  );
}

function LegalPage({ type, sections }) {
  const icon = type === 'privacy' ? ShieldCheck : ClipboardCheck;
  const LegalIcon = icon;

  return (
    <>
      <PageHeader page={type} />
      <section className="section legal-page">
        <div className="legal-title-card">
          <LegalIcon size={28} strokeWidth={2.4} />
          <div>
            <h2>{pageCopy[type].eyebrow}</h2>
            <p>Effective Date: 10 May 2026</p>
          </div>
        </div>
        <div className="legal-stack">
          {sections.map((section) => (
            <article className="legal-page-card" key={section.title}>
              <h3>{section.title}</h3>
              <ul>
                {section.points.map((point) => (
                  <li key={point}>
                    <CheckCircle2 size={16} strokeWidth={2.4} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

export default App;
